/**
 * The eleven states, as keys.
 *
 * The names are the matrix row labels in `design/Oprappo P0 States.dc.html`
 * (lines 689–786), transcribed. `label_bn` is never composed and never translated —
 * it is the drawn string.
 *
 * Nine of the eleven are error conditions and map one-to-one onto
 * `packages/contracts/src/errors.ts`. Two — নতুন ব্যবহারকারী and লোডিং — are not
 * failures and carry no code: an empty list is a beginning, and a skeleton is a
 * moment, so neither has anything for a server to report.
 *
 * The mapping is a bijection over the nine codes, and `states.test.tsx` asserts it in
 * both directions. That is what keeps the absence of a generic code load-bearing: a
 * tenth code could not be added without a state to draw it in, and a twelfth state
 * could not be added without saying which code produces it.
 */
import type { ErrorCode } from '@oprappo/contracts';

export type StateKey =
  | 'new_user'
  | 'loading'
  | 'offline'
  | 'payment_failed'
  | 'provider_unavailable'
  | 'verification_pending'
  | 'sync_error'
  | 'permission_denied'
  | 'service_paused'
  | 'domain_not_live'
  | 'approval_pending';

/**
 * Row order is the matrix's own row order, top to bottom. Do not sort this list: the
 * matrix data below is indexed against it and the design reads down the same column.
 */
export const STATE_KEYS: readonly StateKey[] = [
  'new_user',
  'loading',
  'offline',
  'payment_failed',
  'provider_unavailable',
  'verification_pending',
  'sync_error',
  'permission_denied',
  'service_paused',
  'domain_not_live',
  'approval_pending',
];

/** The drawn row label, exactly as the matrix writes it. */
export const STATE_LABEL_BN: Record<StateKey, string> = {
  new_user: 'নতুন ব্যবহারকারী',
  loading: 'লোডিং · স্কেলিটন',
  offline: 'ইন্টারনেট নেই',
  payment_failed: 'পেমেন্ট ব্যর্থ',
  provider_unavailable: 'প্রদানকারী অনুপলব্ধ',
  verification_pending: 'যাচাই অপেক্ষমাণ',
  sync_error: 'সিঙ্ক ত্রুটি',
  permission_denied: 'অনুমতি নেই',
  service_paused: 'সেবা সাময়িক বন্ধ',
  domain_not_live: 'সেবাটি আসছে',
  approval_pending: 'অনুমোদন অপেক্ষমাণ',
};

/**
 * The nine error codes, keyed by the state they render as. `null` means the state is
 * not an error and no code produces it.
 */
export const STATE_ERROR_CODE: Record<StateKey, ErrorCode | null> = {
  new_user: null,
  loading: null,
  offline: 'E_OFFLINE',
  payment_failed: 'E_PAYMENT_FAILED',
  provider_unavailable: 'E_PROVIDER_UNAVAILABLE',
  verification_pending: 'E_VERIFICATION_PENDING',
  sync_error: 'E_SYNC',
  permission_denied: 'E_PERMISSION',
  service_paused: 'E_SERVICE_PAUSED',
  domain_not_live: 'E_DOMAIN_NOT_LIVE',
  approval_pending: 'E_APPROVAL_PENDING',
};

/**
 * The inverse. An error body arrives with a code; this says which state draws it.
 *
 * There is no default branch and no fallback entry, deliberately. A code that is not
 * in this map is a code that has no drawn state, and the answer to that is a design
 * frame, not a vaguer sentence (docs/10 § WP-K).
 */
export const ERROR_CODE_STATE: Record<ErrorCode, StateKey> = {
  E_OFFLINE: 'offline',
  E_PAYMENT_FAILED: 'payment_failed',
  E_PROVIDER_UNAVAILABLE: 'provider_unavailable',
  E_VERIFICATION_PENDING: 'verification_pending',
  E_SYNC: 'sync_error',
  E_PERMISSION: 'permission_denied',
  E_SERVICE_PAUSED: 'service_paused',
  E_APPROVAL_PENDING: 'approval_pending',
  E_DOMAIN_NOT_LIVE: 'domain_not_live',
};

export function isStateKey(value: string): value is StateKey {
  return (STATE_KEYS as readonly string[]).includes(value);
}
